// Chart maths, kept free of the DOM so it can be tested: nice ticks, scales and min/max decimation
// (a 500 Hz EEG channel has 30,000 samples for a few hundred pixels; each pixel column keeps its
// extremes, so spikes survive).

export interface Scale {
  domain: [number, number];
  range: [number, number];
  log?: boolean;
}

export function scale(s: Scale, v: number): number {
  const [d0, d1] = s.log ? [Math.log10(s.domain[0]), Math.log10(s.domain[1])] : s.domain;
  const x = s.log ? Math.log10(Math.max(v, 1e-300)) : v;
  const t = d1 === d0 ? 0.5 : (x - d0) / (d1 - d0);
  return s.range[0] + t * (s.range[1] - s.range[0]);
}

export function invert(s: Scale, px: number): number {
  const t = s.range[1] === s.range[0] ? 0 : (px - s.range[0]) / (s.range[1] - s.range[0]);
  if (s.log) {
    const [d0, d1] = [Math.log10(s.domain[0]), Math.log10(s.domain[1])];
    return 10 ** (d0 + t * (d1 - d0));
  }
  return s.domain[0] + t * (s.domain[1] - s.domain[0]);
}

/** About `count` round ticks (1, 2 or 5 × 10^k steps) inside [lo, hi]; at least two where a round step allows. */
export function niceTicks(lo: number, hi: number, count = 4): number[] {
  if (!(hi > lo) || !Number.isFinite(lo) || !Number.isFinite(hi)) return Number.isFinite(lo) ? [lo] : [];
  const raw = (hi - lo) / Math.max(1, count);
  const mag = 10 ** Math.floor(Math.log10(raw));
  const steps = [0.5, 1, 2, 5, 10].map((m) => m * mag);
  const ticks = (step: number) => {
    const out: number[] = [];
    for (let v = Math.ceil(lo / step - 1e-9) * step; v <= hi + step * 1e-9; v += step) out.push(Math.abs(v) < step * 1e-9 ? 0 : Number(v.toPrecision(12)));
    return out;
  };
  // The first round step at least `raw`; a smaller one when that leaves a lane with a single tick.
  let i = steps.findIndex((s) => s >= raw);
  if (i < 0) i = steps.length - 1;
  let out = ticks(steps[i]!);
  while (out.length < 2 && i > 0) out = ticks(steps[--i]!);
  return out;
}

/** An x axis's labels: its tick text and, on a strip with two clocks, its name and what it counts from. */
export interface XAxis {
  format: (x: number) => string;
  /** In the gutter beside its ticks, in the colour of what it times. */
  name?: { text: string; colour: string };
  /** What its values count from, at the right end of its row ('from clinical onset'). */
  title?: string;
}

/** A second clock over the same data: its own value at data x is x + `shift`, and it runs over `extent`. */
export interface SecondClock extends XAxis {
  shift: number;
  extent?: [number, number];
}

/** A second clock's tick values (its own units) over the data window, inside its extent. */
export function clockTicks(c: SecondClock, domain: [number, number], count: number): number[] {
  const width = domain[1] - domain[0];
  const lo = Math.max(domain[0] + c.shift, c.extent?.[0] ?? -Infinity);
  const hi = Math.min(domain[1] + c.shift, c.extent?.[1] ?? Infinity);
  if (!(width > 0) || !(hi > lo)) return [];
  return niceTicks(lo, hi, Math.max(1, Math.round((count * (hi - lo)) / width)));
}

/** The hover readout's time: the one clock, or both by name where the second runs ('Patient −3.2 s · Model 16.4 s'). */
export function readoutX(x: XAxis, x2: SecondClock | undefined, v: number): string {
  const named = (a: XAxis, value: number) => (a.name ? `${a.name.text} ${a.format(value)}` : a.format(value));
  if (!x2) return x.format(v);
  const own = v + x2.shift;
  const runs = !x2.extent || (own >= x2.extent[0] - 1e-9 && own <= x2.extent[1] + 1e-9);
  return runs ? `${named(x, v)} · ${named(x2, own)}` : named(x, v);
}

/** Decades inside [lo, hi] for a log axis (1, 10, 100…). */
export function logTicks(lo: number, hi: number): number[] {
  const out: number[] = [];
  for (let e = Math.ceil(Math.log10(lo) - 1e-9); e <= Math.floor(Math.log10(hi) + 1e-9); e++) out.push(10 ** e);
  return out;
}

/** The first index with xs[i] ≥ x (xs increasing). */
export function lowerBound(xs: ArrayLike<number>, x: number): number {
  let lo = 0;
  let hi = xs.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (xs[mid]! < x) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** y at x by linear interpolation (xs increasing); NaN outside. */
export function valueAt(xs: ArrayLike<number>, ys: ArrayLike<number>, x: number): number {
  const n = xs.length;
  if (!n || x < xs[0]! || x > xs[n - 1]!) return NaN;
  const i = Math.min(n - 1, Math.max(1, lowerBound(xs, x)));
  const x0 = xs[i - 1]!;
  const x1 = xs[i]!;
  const w = x1 > x0 ? (x - x0) / (x1 - x0) : 0;
  return ys[i - 1]! + (ys[i]! - ys[i - 1]!) * w;
}

/**
 * Points to draw for xs/ys over [x0, x1] at `px` pixel columns. Sparse data comes back as is (with
 * one point either side, so lines reach the edges); dense data keeps first, min, max and last of
 * each column, in x order.
 */
export function decimate(xs: ArrayLike<number>, ys: ArrayLike<number>, x0: number, x1: number, px: number, xScale?: (x: number) => number): { x: number[]; y: number[] } {
  const n = xs.length;
  const a = Math.max(0, lowerBound(xs, x0) - 1);
  const b = Math.min(n, lowerBound(xs, x1) + 1);
  const outX: number[] = [];
  const outY: number[] = [];
  const cols = Math.max(1, Math.round(px));
  if (b - a <= cols * 2) {
    for (let i = a; i < b; i++) {
      outX.push(xs[i]!);
      outY.push(ys[i]!);
    }
    return { x: outX, y: outY };
  }
  const toCol = xScale ?? ((x: number) => ((x - x0) / (x1 - x0)) * cols);
  let col = Math.floor(toCol(xs[a]!));
  let first = a;
  let lo = a;
  let hi = a;
  const flush = (last: number) => {
    const idx = [first, lo, hi, last].filter((v, i, all) => all.indexOf(v) === i).sort((p, q) => p - q);
    for (const i of idx) {
      outX.push(xs[i]!);
      outY.push(ys[i]!);
    }
  };
  for (let i = a + 1; i < b; i++) {
    const c = Math.floor(toCol(xs[i]!));
    if (c !== col) {
      flush(i - 1);
      col = c;
      first = lo = hi = i;
      continue;
    }
    if (ys[i]! < ys[lo]!) lo = i;
    if (ys[i]! > ys[hi]!) hi = i;
  }
  flush(b - 1);
  return { x: outX, y: outY };
}

/** Finite min and max of some arrays (NaN-safe); [0, 1] when there's nothing finite. */
export function extent(arrays: ArrayLike<number>[]): [number, number] {
  let lo = Infinity;
  let hi = -Infinity;
  for (const a of arrays)
    for (let i = 0; i < a.length; i++) {
      const v = a[i]!;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
  return Number.isFinite(lo) && Number.isFinite(hi) ? [lo, hi] : [0, 1];
}

/** Pad a [lo, hi] range by a fraction, keeping 0 as a floor when the data starts there. */
export function padded([lo, hi]: [number, number], frac = 0.08): [number, number] {
  if (hi === lo) return [lo - 1, hi + 1];
  const p = (hi - lo) * frac;
  return [lo >= 0 && lo - p < 0 ? 0 : lo - p, hi + p];
}

/**
 * Left edges for labels anchored at `x` with width `w`, kept inside [lo, hi]: centred on their
 * anchor, or (`start`) just right of it, flipping left near the end. A label that would overlap
 * one already placed, or a `taken` span, gets null: its marker stays, its text doesn't. Lower ranks
 * are placed first, left to right within a rank, so a spike's name wins over the stimulus before it.
 */
export function placeLabels(items: { x: number; w: number; rank?: number }[], lo: number, hi: number, opts: { anchor?: 'center' | 'start'; gap?: number; taken?: [number, number][] } = {}): (number | null)[] {
  const gap = opts.gap ?? 8;
  const placed: [number, number][] = [...(opts.taken ?? [])];
  const out: (number | null)[] = items.map(() => null);
  const order = items.map((_, i) => i).sort((a, b) => (items[a]!.rank ?? 0) - (items[b]!.rank ?? 0) || items[a]!.x - items[b]!.x);
  for (const i of order) {
    const { x, w } = items[i]!;
    // Nothing to place: a tick with no label of its own.
    if (!(w > 0)) continue;
    let left = opts.anchor === 'start' ? (x + 4 + w > hi ? x - 4 - w : x + 4) : x - w / 2;
    left = Math.max(lo, Math.min(hi - w, left));
    if (placed.some(([a, b]) => left < b + gap && left + w > a - gap)) continue;
    placed.push([left, left + w]);
    out[i] = left;
  }
  return out;
}

/** A tick label: as short as the value allows (0.75, 10, 1.5k never; ticks are round already). */
export function formatTick(v: number): string {
  if (!Number.isFinite(v)) return '';
  if (v === 0) return '0';
  const a = Math.abs(v);
  if (a >= 1e5 || a < 1e-3) return v.toExponential(0);
  return String(Number(v.toPrecision(6)));
}

/** A number for a readout: 3 significant figures, no exponent for everyday magnitudes. */
export function formatValue(v: number, digits = 3): string {
  if (!Number.isFinite(v)) return '–';
  const a = Math.abs(v);
  if (a === 0) return '0';
  if (a >= 1e5 || a < 1e-3) return v.toExponential(digits - 1);
  const decimals = Math.max(0, digits - 1 - Math.floor(Math.log10(a)));
  return v.toFixed(Math.min(decimals, 4));
}
